import { EventType } from '../experiments/Events.ts';
import type { EventLogger } from '../experiments/EventLogger.ts';
import type { GameState } from '../game/GameState.ts';
import type { ButtonEvent, InputDevice } from '../hardware/InputDevice.ts';
import type { StimulationService } from '../stimulation/StimulationService.ts';
import type { BotController } from './BotController.ts';
import { EdgeLatch, noFlap, type Controller, type ControllerOutput } from './Controller.ts';

export interface StimulationControllerOptions {
  /** Pulse duration asked for on each request, ms. Clamped by `SafetyGate`. */
  stimulusDurationMs: number;
  /**
   * How long to wait for a movement before writing the request off as missed.
   * Should be comfortably above the p95 response latency from calibration.
   */
  responseTimeoutMs: number;
  /**
   * Inputs arriving sooner than this after stimulus onset are not attributed to
   * it -- nobody's motor system is that fast, so it was a twitch or a bounce.
   */
  minAttributionMs: number;
  /** Hardware channel to stimulate. */
  channel: number;
}

export const DEFAULT_STIM_CONTROLLER_OPTIONS: StimulationControllerOptions = {
  stimulusDurationMs: 60,
  responseTimeoutMs: 400,
  minAttributionMs: 15,
  channel: 0,
};

interface OpenRequest {
  requestId: string;
  issuedAt: number;
  tick: number;
  reason: string;
}

export interface MediationCounters {
  intents: number;
  requested: number;
  rejected: number;
  movements: number;
  missed: number;
  unattributed: number;
  flaps: number;
}

/**
 * Human-mediated control: the mode the whole project exists to support.
 *
 *   bot observes the game
 *     -> bot decides "flap"                      BOT_FLAP_REQUESTED
 *     -> software asks for a stimulus            STIM_REQUESTED
 *     -> stimulator fires                        STIM_STARTED / STIM_ENDED
 *     -> the participant's hand moves            (not observable in software)
 *     -> the movement trips a switch or EMG      BUTTON_DOWN
 *     -> the game flaps                          FLAP_EXECUTED
 *
 * The design rule that matters more than any other in this file: **the return
 * value of `update()` is derived from the input device, and from nothing else.**
 * The bot's decision goes out through `StimulationService`; it never touches
 * the flap that comes back. If the stimulator is a `MockStimulator`, or the
 * participant does not respond, or the switch is unplugged, the bird falls.
 * That is the measurement.
 *
 * The same class covers three of the experimental conditions, differing only in
 * which `Stimulator` the service holds:
 *
 *   - `MockStimulator`            intent with no actuator at all
 *   - `SimulatedHumanStimulator`  intent through a synthetic delay + failure rate
 *   - `BackyardBrainsHHIAdapter`  intent through a real participant
 *
 * No code in this file knows which of those is installed.
 */
export class StimulationController implements Controller {
  readonly id: string;
  readonly label: string;

  options: StimulationControllerOptions;

  private latch = new EdgeLatch();
  private unsubscribe: (() => void) | null = null;
  private open: OpenRequest[] = [];
  private counters: MediationCounters = {
    intents: 0, requested: 0, rejected: 0, movements: 0,
    missed: 0, unattributed: 0, flaps: 0,
  };
  private lastAttributedRequestId: string | null = null;
  /**
   * Monotonic request counter. Deliberately NOT cleared by `reset()`: ticks
   * restart at zero on every run, so a per-run counter would mint the same
   * request id in run 1 and run 3 and any offline analysis that groups by
   * requestId would splice two runs into one chain with a 40-second latency.
   */
  private sequence = 0;

  constructor(
    readonly bot: BotController,
    protected readonly service: StimulationService,
    protected readonly device: InputDevice,
    protected readonly logger: EventLogger,
    protected readonly getTick: () => number,
    options: Partial<StimulationControllerOptions> = {},
    id = 'stimulation',
    label = 'Bot → stimulation → participant → button',
  ) {
    this.options = { ...DEFAULT_STIM_CONTROLLER_OPTIONS, ...options };
    this.id = id;
    this.label = label;
  }

  async attach(): Promise<void> {
    if (this.unsubscribe) return;
    if (this.device.getStatus() !== 'connected') await this.device.connect();
    this.unsubscribe = this.device.onButton((e) => this.onButton(e));
  }

  async detach(): Promise<void> {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.latch.clear();
    this.open = [];
  }

  reset(): void {
    this.latch.clear();
    this.open = [];
    this.bot.reset();
    this.counters = {
      intents: 0, requested: 0, rejected: 0, movements: 0,
      missed: 0, unattributed: 0, flaps: 0,
    };
  }

  /**
   * One simulation tick.
   *
   * Order is deliberate and was chosen to match the offline benchmark:
   *   1. decide (the bot still counts any impulse it is waiting for)
   *   2. dispatch the request, if any
   *   3. only then consume detected inputs into this tick's flap
   *
   * Deciding first means an impulse that lands on this very tick is still
   * modelled as "in flight, arriving now" rather than as "already applied",
   * which is what keeps the bot from double-requesting on a late response.
   */
  update(state: GameState): ControllerOutput {
    const now = state.timestamp;

    this.expireOpenRequests(now, state);

    // ---- 1. intent -------------------------------------------------------
    const decision = this.bot.decide(state, now);
    if (decision.flap) {
      this.dispatch(state, decision.reason, now);
    }

    // ---- 2. execution ----------------------------------------------------
    const edge = this.latch.consume();
    if (!edge) {
      return noFlap({ decision, open: this.open.length });
    }

    const requestId = (edge.meta?.requestId as string | undefined) ?? undefined;
    if (requestId) {
      this.bot.noteExecuted(requestId);
      this.open = this.open.filter((o) => o.requestId !== requestId);
    } else {
      this.bot.noteAnyExecuted();
    }
    this.counters.flaps++;

    return {
      flap: true,
      timestamp: edge.timestamp,
      metadata: { decision, requestId, source: edge.meta?.source },
    };
  }

  status(): Record<string, unknown> {
    return {
      ...this.counters,
      openRequests: this.open.length,
      device: this.device.id,
      connection: this.device.getStatus(),
      stimulator: this.service.getStatus().kind,
      armed: this.service.getStatus().armed,
      lastAttributed: this.lastAttributedRequestId,
    };
  }

  getCounters(): MediationCounters {
    return { ...this.counters };
  }

  /** Requests issued but not yet answered -- drawn on the dashboard. */
  get openRequestCount(): number {
    return this.open.length;
  }

  // ------------------------------------------------------------------ intent

  private dispatch(state: GameState, reason: string, now: number): void {
    const requestId = `req-${this.sequence++}-t${state.tick}`;
    this.counters.intents++;

    this.logger.log(EventType.BOT_FLAP_REQUESTED, {
      tick: state.tick,
      timestamp: now,
      simTimeMs: state.simTimeMs,
      gameState: state,
      requestId,
      metadata: { decision: this.bot.lastDecision, reason },
    });

    // The bot must believe an impulse is coming, or it will keep asking.
    // The ETA it uses is its own configured latency estimate, which is the
    // measured median from calibration -- not ground truth, on purpose.
    this.bot.noteIntent(requestId, now, this.bot.params.latencyMs);
    this.open.push({ requestId, issuedAt: now, tick: state.tick, reason });
    this.counters.requested++;

    // Fire and forget. Awaiting here would stall the simulation loop on a
    // serial round-trip, and the response does not arrive through this promise
    // anyway -- it arrives as a button event, later, if at all.
    void this.service
      .requestStimulus({
        requestId,
        tick: state.tick,
        simTimeMs: state.simTimeMs,
        gameState: state,
        channel: this.options.channel,
        durationMs: this.options.stimulusDurationMs,
        reason: `bot: ${reason}`,
      })
      .then((result) => {
        if (!result.accepted) {
          this.counters.rejected++;
          this.bot.noteExecuted(requestId);
          this.open = this.open.filter((o) => o.requestId !== requestId);
        }
      });
  }

  // --------------------------------------------------------------- execution

  /**
   * Attribute a detected input to a stimulation request.
   *
   * A simulated participant reports which request it is answering. A real
   * switch cannot, so the oldest open request within the plausible window gets
   * it. Anything with no plausible parent is logged as INPUT_FALSE_POSITIVE and
   * still flaps the bird -- because it really did close the switch, and hiding
   * that would misrepresent what the participant's arm did.
   */
  protected onButton(e: ButtonEvent): void {
    if (!e.down) {
      this.logger.log(EventType.BUTTON_UP, {
        tick: this.getTick(),
        timestamp: e.timestamp,
        requestId: e.requestId,
        metadata: { source: e.source },
      });
      return;
    }

    let requestId = e.requestId;
    if (!requestId) {
      const candidate = this.open.find((o) => {
        const dt = e.timestamp - o.issuedAt;
        return dt >= this.options.minAttributionMs && dt <= this.options.responseTimeoutMs;
      });
      requestId = candidate?.requestId;
    }

    if (requestId) {
      this.counters.movements++;
      this.lastAttributedRequestId = requestId;
    } else {
      this.counters.unattributed++;
      this.logger.log(EventType.INPUT_FALSE_POSITIVE, {
        tick: this.getTick(),
        timestamp: e.timestamp,
        metadata: { source: e.source, openRequests: this.open.length },
      });
    }

    this.logger.log(EventType.BUTTON_DOWN, {
      tick: this.getTick(),
      timestamp: e.timestamp,
      requestId,
      metadata: { source: e.source, attributed: Boolean(requestId), ...e.metadata },
    });

    this.latch.set(e.timestamp, { source: e.source, requestId });
  }

  private expireOpenRequests(now: number, state: GameState): void {
    if (this.open.length === 0) return;
    const timeout = this.options.responseTimeoutMs;
    const expired = this.open.filter((o) => now - o.issuedAt > timeout);
    if (expired.length === 0) return;
    this.open = this.open.filter((o) => now - o.issuedAt <= timeout);
    for (const o of expired) {
      this.counters.missed++;
      this.bot.noteExecuted(o.requestId);
      this.logger.log(EventType.STIM_MISSED, {
        tick: state.tick,
        timestamp: now,
        simTimeMs: state.simTimeMs,
        requestId: o.requestId,
        metadata: { waitedMs: now - o.issuedAt, timeoutMs: timeout, reason: o.reason },
      });
    }
  }
}
